import { UserRole } from './roles'

// Base entity types
export interface BaseEntity {
  createdAt: string
  updatedAt: string
}

// Applicant statuses
export enum ApplicantStatus {
  REGISTERED = 'REGISTERED',
  VIDEO_UPLOADED = 'VIDEO_UPLOADED',
  INTERVIEW_SCHEDULED = 'INTERVIEW_SCHEDULED',
  UNDER_REVIEW = 'UNDER_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  REVISION_REQUIRED = 'REVISION_REQUIRED',
}

// Qualification path
export type QualificationPath = 'VIDEO' | 'INTERVIEW'

// Applicant entity
export interface Applicant extends BaseEntity {
  id: string
  email: string
  passwordHash: string
  firstName: string
  lastName: string
  phone: string
  status: ApplicantStatus
  qualificationPath?: QualificationPath
  scriptVersion?: string
  scriptDownloadedAt?: string
  videoUploadedAt?: string
  videoUrl?: string
  interviewId?: string
  interviewScheduledAt?: string
  reviewedBy?: string
  reviewedAt?: string
  reviewNotes?: string
}

// Scout entity
export interface Scout extends BaseEntity {
  id: string
  applicantId: string
  email: string
  firstName: string
  lastName: string
  phone: string
  referralCode: string
  referralUrl: string
  qrCodeUrl: string
  isActive: boolean
  approvedAt: string
  approvedBy: string
}

// Business statuses
export enum BusinessStatus {
  AVAILABLE = 'AVAILABLE',
  RESERVED = 'RESERVED',
  VISITED = 'VISITED',
  REGISTERED = 'REGISTERED',
  INACTIVE = 'INACTIVE',
}

// Business entity
export interface Business extends BaseEntity {
  id: string
  name: string
  address: string
  city: string
  postalCode: string
  areaId: string
  latitude: number
  longitude: number
  category?: string
  phone?: string
  website?: string
  status: BusinessStatus
  reservedBy?: string
  reservedAt?: string
  reservationExpiresAt?: string
  lastVisitedAt?: string
  registeredAt?: string
  merchantId?: string
  osmId?: string
}

// Area entity
export interface Area extends BaseEntity {
  id: string
  name: string
  cityId: string
  description?: string
  isActive: boolean
}

// City entity
export interface City extends BaseEntity {
  id: string
  name: string
  regionId: string
  stateId: string
}

// Region entity
export interface Region extends BaseEntity {
  id: string
  name: string
  stateId: string
}

// State entity
export interface State extends BaseEntity {
  id: string
  name: string
  code: string
}

// Visit outcome types
export enum VisitOutcome {
  INTERESTED = 'INTERESTED',
  NOT_INTERESTED = 'NOT_INTERESTED',
  CALLBACK_REQUIRED = 'CALLBACK_REQUIRED',
  REGISTERED = 'REGISTERED',
  CLOSED = 'CLOSED',
  NOT_FOUND = 'NOT_FOUND',
}

// Visit entity
export interface Visit extends BaseEntity {
  id: string
  scoutId: string
  businessId: string
  businessName?: string
  startTime: string
  endTime?: string
  duration?: number // in seconds
  startLatitude: number
  startLongitude: number
  endLatitude?: number
  endLongitude?: number
  distanceMeters?: number
  distanceOverridden?: boolean
  notes?: string
  outcome?: VisitOutcome
  photoUrls?: string[]
  isCompleted: boolean
  rejected?: boolean
  rejectedAt?: string
  rejectedBy?: string
  rejectionReason?: string
}

// Photo entity
export interface VisitPhoto {
  id: string
  visitId: string
  url: string
  uploadedAt: string
  latitude?: number
  longitude?: number
}

// Merchant Registration (from referral)
export interface MerchantRegistration extends BaseEntity {
  id: string
  businessId: string
  scoutId: string
  referralCode: string
  merchantEmail: string
  merchantName: string
  registeredAt: string
  source: 'QR_CODE' | 'LINK' | 'MANUAL'
  ipAddress?: string
  userAgent?: string
}

// Referral Click Tracking
export interface ReferralClick extends BaseEntity {
  id: string
  scoutId: string
  referralCode: string
  timestamp: string
  ipAddress?: string
  userAgent?: string
  converted: boolean
  merchantId?: string
}

// Compensation entity
export interface Compensation extends BaseEntity {
  id: string
  scoutId: string
  period: string // YYYY-MM format
  hourlyRate: number
  hoursWorked: number
  hourlyPay: number
  registrationCount: number
  registrationBonus: number
  bonusPerRegistration: number
  totalAmount: number
  status: CompensationStatus
  calculatedAt: string
  approvedBy?: string
  approvedAt?: string
  paidAt?: string
}

export enum CompensationStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  PAID = 'PAID',
}

// Interview status
export enum InterviewStatus {
  SCHEDULED = 'SCHEDULED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  NO_SHOW = 'NO_SHOW',
}

// Interview checklist rating
export type ChecklistRating = 'BESTANDEN' | 'TEILWEISE' | 'NICHT_BESTANDEN'

// Interview checklist item
export interface InterviewChecklistItem {
  id: string
  label: string
  category: string
  rating: ChecklistRating | null
  notes?: string
}

// Default checklist template
export const INTERVIEW_CHECKLIST_TEMPLATE: Omit<InterviewChecklistItem, 'rating' | 'notes'>[] = [
  { id: 'greeting', label: 'Begrüßung & Auftreten', category: 'Ersteindruck' },
  { id: 'product_knowledge', label: 'Produktverständnis', category: 'Wissen' },
  { id: 'sales_pitch', label: 'Verkaufsgespräch', category: 'Verkauf' },
  { id: 'objection_handling', label: 'Einwandbehandlung', category: 'Verkauf' },
  { id: 'closing', label: 'Abschlussstärke', category: 'Verkauf' },
  { id: 'motivation', label: 'Motivation & Zuverlässigkeit', category: 'Gesamteindruck' },
]

// Interview entity
export interface Interview extends BaseEntity {
  id: string
  applicantId: string
  applicantName?: string
  applicantEmail?: string
  interviewerId: string
  interviewerName: string
  scheduledAt: string
  durationMinutes: number
  meetLink: string
  moderatorMeetLink?: string
  roomName?: string
  googleCalendarEventId: string
  status: InterviewStatus
  checklist: InterviewChecklistItem[]
  result?: ApplicantStatus.APPROVED | ApplicantStatus.REJECTED | ApplicantStatus.REVISION_REQUIRED
  reviewNotes?: string
  reviewedAt?: string
  schedulerArns: string[]
}

// Interviewer weekly time slot
export interface WeeklyTimeSlot {
  startTime: string // HH:mm
  endTime: string   // HH:mm
  slotDurationMinutes: number
}

// Interviewer availability (recurring pattern)
export interface InterviewerAvailability extends BaseEntity {
  interviewerId: string
  googleEmail: string
  displayName: string
  weeklyPattern: Record<string, WeeklyTimeSlot[]> // keys: "0"-"6" (Sun-Sat)
  isActive: boolean
}

// Availability override type
export type AvailabilityOverrideType = 'BLOCKED' | 'ADDED'

// Availability override for a specific date
export interface AvailabilityOverride extends BaseEntity {
  interviewerId: string
  date: string // YYYY-MM-DD
  type: AvailabilityOverrideType
  slots?: { startTime: string; endTime: string }[]
}

// Audit event types
export enum AuditEventType {
  USER_LOGIN = 'USER_LOGIN',
  USER_REGISTRATION = 'USER_REGISTRATION',
  APPLICANT_APPROVED = 'APPLICANT_APPROVED',
  APPLICANT_REJECTED = 'APPLICANT_REJECTED',
  BUSINESS_RESERVED = 'BUSINESS_RESERVED',
  VISIT_CREATED = 'VISIT_CREATED',
  VISIT_COMPLETED = 'VISIT_COMPLETED',
  MERCHANT_REGISTERED = 'MERCHANT_REGISTERED',
  COMPENSATION_CALCULATED = 'COMPENSATION_CALCULATED',
  COMPENSATION_APPROVED = 'COMPENSATION_APPROVED',
  PAYOUT_COMPLETED = 'PAYOUT_COMPLETED',
  SCRIPT_DOWNLOADED = 'SCRIPT_DOWNLOADED',
  VIDEO_UPLOADED = 'VIDEO_UPLOADED',
  INTERVIEW_BOOKED = 'INTERVIEW_BOOKED',
  INTERVIEW_COMPLETED = 'INTERVIEW_COMPLETED',
  INTERVIEW_CANCELLED = 'INTERVIEW_CANCELLED',
}

// Audit entity
export interface AuditEvent extends BaseEntity {
  id: string
  eventType: AuditEventType
  entityType: string
  entityId: string
  userId: string
  userRole: UserRole
  metadata: Record<string, unknown>
  ipAddress?: string
  userAgent?: string
}

// Import batch entity
export interface ImportBatch extends BaseEntity {
  id: string
  type: 'BUSINESSES' | 'REGIONS'
  fileName: string
  uploadedBy: string
  totalRecords: number
  successCount: number
  failureCount: number
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
  errorLog?: string
  completedAt?: string
}
